import { type InputValidationState } from '@landsbankinn/ui-kit';
import { useCallback, useReducer } from 'react';
import {
  useApplicationServerContext,
  type LoginServerState,
} from 'src/state/server';

export function usePasswordValidation() {
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  type State = {
    username: {
      value: string;
      validationState?: InputValidationState;
    };
    password: {
      value: string;
      validationState?: InputValidationState;
    };
    xsrf: string;
  };
  type Action =
    | {
        type: 'on change username';
        value: string;
        validationState: InputValidationState;
      }
    | {
        type: 'on change password';
        value: string;
        validationState: InputValidationState;
      }
    | {
        type: 'on xsrf token';
        value: string;
      };
  const [state, dispatch] = useReducer(
    function formReducer(formState: State, action: Action): State {
      switch (action.type) {
        case 'on change username': {
          return {
            ...formState,
            username: {
              value: action.value,
              validationState: action.validationState,
            },
          };
        }
        case 'on change password': {
          return {
            ...formState,
            password: {
              value: action.value,
              validationState: action.validationState,
            },
          };
        }
        case 'on xsrf token': {
          return {
            ...formState,
            xsrf: action.value,
          };
        }
        default:
          return formState;
      }
    },
    {
      username: {
        value: '',
        validationState: 'unset',
      },
      password: {
        value: '',
        validationState: 'unset',
      },
      xsrf: serverState.forms.password.fields.xsrf.value || '',
    },
  );

  const validateUsername = useCallback(function validate(
    username?: string | undefined,
  ): InputValidationState {
    if (username === undefined || username === '') {
      return 'invalid';
    } else {
      return 'valid';
    }
  }, []);
  const validatePassword = useCallback(function validate(
    password?: string | undefined,
  ): InputValidationState {
    if (password === undefined || password === '') {
      return 'invalid';
    } else {
      return 'valid';
    }
  }, []);
  const onUsernameChanged = useCallback(
    function onUsernameChanged(value: string) {
      const validationState = validateUsername(value);
      // dispatch for the actual input
      dispatch({
        type: 'on change username',
        value: value,
        validationState: validationState,
      });
    },
    [validateUsername],
  );
  const onPasswordChanged = useCallback(
    function onPasswordChanged(value: string) {
      const validationState = validatePassword(value);
      // dispatch for the actual input
      dispatch({
        type: 'on change password',
        value: value,
        validationState: validationState,
      });
    },
    [validatePassword],
  );

  const onXsrf = useCallback(function onXsrfChanged(xsrf: string) {
    dispatch({ type: 'on xsrf token', value: xsrf });
  }, []);

  return {
    onUsernameChanged,
    onPasswordChanged,
    onXsrf,
    passwordState: state,
  };
}
